import { Content } from "@r8er/components/marketing/content";
import styles from "@r8er/components/marketing/content/content.module.css";
import { CtaBlock, Ctas, SignUpButton } from "@r8er/components/marketing/ctas";
import { Hero } from "@r8er/components/marketing/hero";
import { Layout } from "@r8er/components/marketing/layout";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import Head from "next/head";

export default function Entrepreneurs() {
  return (
    <div>
      <Head>
        <title>Entrepreneurs - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Hero isSlim title="Entrepreneurs" img="/entrepreneurs-hero.png" />

        <Content isFullWidth isCentered style={{ paddingBottom: 0 }}>
          <p
            className="p--standard"
            style={{
              fontSize: "calc(1.65 * var(--font-size-base))",
              margin: "0 auto",
              letterSpacing: "-.25px",
            }}
          >
            For R8ER-registered entrepreneurs, our team of experts will provide
            valuable insights to ensure the best possible chance of securing
            investment via our investor community. Once finalized, your
            company's profile will be directly available to R8ER investors for
            the period of one full year.
          </p>
        </Content>
        <Content
          isFullWidth
          isNarrow
          className={styles.topSection}
          isCentered
          style={{ paddingTop: 0 }}
        >
          <SignUpButton text="Apply Now" />
        </Content>

        <Content
          isGrey
          backgroundImage="/entrepreneurs-how.png"
          minHeight={700}
        >
          <h2 className="h2--standard">
            Raise Investment Through the Right Investors
          </h2>
          <p className="p--standard">
            R8ER helps innovative health and climate tech companies become
            investment-ready and connect with qualified investors worldwide.
          </p>
          <p className="p--standard">
            Our investor network includes family offices, venture capital funds,
            institutional investors and high-net-worth individuals. Family
            offices can often be one of the most valuable sources of long-term
            capital, but they can also be among the hardest investors for
            founders to identify and access. R8ER has become a trusted platform
            for family offices seeking professionally reviewed investment
            opportunities, helping companies reach investors who are often
            inaccessible through traditional fundraising routes.
          </p>
          <p className="p--standard">
            Investment opportunities across our network range from £50,000 to
            £10 million.
          </p>
        </Content>

        <section id="how-r8er-works" className={styles.howWorks}>
          <h2 className={"h2--standard " + styles.howStepsHeading}>
            How R8ER Works
          </h2>
        </section>

        <Content
          isRightAligned
          backgroundImage="/entrepreneurs-step-apply.webp"
          minHeight={420}
          className={`${styles.howStepSection} ${styles.howStepSectionFirst}`}
        >
          <h2 className="h2--standard">Step 1 &ndash; Apply</h2>
          <p className="p--standard">
            Complete our streamlined application and submit your company
            information, pitch deck and short founder video.
          </p>
        </Content>

        <Content
          backgroundImage="/entrepreneurs-step-insight.webp"
          minHeight={420}
          className={styles.howStepSection}
        >
          <h2 className="h2--standard">Step 2 &ndash; Gain Expert Insight</h2>
          <p className="p--standard">
            Your company is matched with five internationally recognised
            specialists and Key Opinion Leaders (selected specifically for their
            expertise in your technology, sector and stage of development),
            bringing together expertise across investment, science, clinical
            practice, regulation, commercialisation and industry.
          </p>
          <p className="p--standard">
            Together, they provide practical observations, recommendations and
            specialist insight, which we bring together into your personalised
            &ldquo;Investment Readiness Report&rdquo;. This is designed to help
            strengthen your fundraising strategy and prepare you for investor
            discussions.
          </p>
          <p className="p--standard">
            Following this process, you decide whether you would like your
            company introduced to relevant investors through the platform.
          </p>
        </Content>

        <Content
          isRightAligned
          backgroundImage="/entrepreneurs-step-investors.webp"
          minHeight={420}
          className={styles.howStepSection}
        >
          <h2 className="h2--standard">
            Step 3 &ndash; Meet Relevant Investors
          </h2>
          <p className="p--standard">
            We only introduce your company to investors whose investment
            preferences match your opportunity.
          </p>
          <p className="p--standard">
            Our investor community includes family offices, VC and PE funds,
            institutional investors, private investment firms and high-net-worth
            individuals.
          </p>
          <p className="p--standard">
            Selected companies may also be introduced directly to our dedicated
            investment funds, including the Vetted Investments Syndicate and the
            Commonwealth Healthy Ageing Investment Fund. Where there is a strong
            fit, this can accelerate your route to investment committee review,
            alongside introductions to our wider investor network.
          </p>
        </Content>

        <Content
          isBlue
          backgroundImage="/entrepreneurs-negotiation.png"
          minHeight={700}
        >
          <h2 className="h2--standard">More Than Investment</h2>
          <p className="p--standard">
            As a R8ER company you'll also benefit from:
          </p>
          <ul className={`ul--standard ${styles.compactMarketingList}`}>
            <li>Monthly pitching sessions and investor events</li>
            <li>MegaPitch showcase events</li>
            <li>Our optional Getting Ready to Raise seminar</li>
            <li>Educational resources</li>
            <li>Board and advisory introductions</li>
            <li>
              Connect Partners including corporates, manufacturers, hospitals
              and strategic organisations
            </li>
            <li>
              Benefit Partners offering services across legal, finance,
              insurance and accounting
            </li>
          </ul>
        </Content>

        <Content
          isGrey
          isRightAligned
          innerImage="/entrepreneurs-report.png"
          minHeight={600}
        >
          <h2 className="h2--standard">What Does It Cost?</h2>
          <p className="p--standard">
            There is a £1,500 application fee, which covers the expert insight
            process and your personalised Investment Readiness Report. In
            October, this will be increasing to £3,000.
          </p>
          <p className="p--standard">
            If you successfully raise investment through a R8ER investor, a 3.6%
            success fee applies.
          </p>
        </Content>

        <Content
          backgroundImage="/entrepreneurs-investment.png"
          minHeight={600}
          className={styles.afterInvestmentSection}
        >
          <h2 className="h2--standard">After Investment</h2>
          <p className="p--standard">
            Our relationship doesn't end once you've secured funding.
          </p>
          <p className="p--standard">
            You'll continue to benefit from investor exposure, future
            fundraising opportunities, platform resources and our growing
            network of partners to help support your company's growth.
          </p>
        </Content>

        <Ctas>
          <CtaBlock
            title="Sign Up to become a R8ER Entrepreneur"
            img="/cta-entrepreneurs.png"
            ctaItems={[{ text: "Apply Now", link: "/auth/signup" }]}
          />
          <CtaBlock
            title="Speak to a member of the R8ER team"
            img="/cta-contact.png"
            ctaItems={[
              { text: "Contact Us", link: "/contact" },
              { text: "Request information", link: "/contact" },
            ]}
            isLight
          />
        </Ctas>

        <Newsletter />
      </Layout>
    </div>
  );
}
